import { useEffect, useState } from "react";
import { Badge, Button, Card, Divider, Spinner, Switch, Text } from "@fluentui/react-components";
import type { MeetingEvent } from "../services/graphService";
import { mainAppUrl } from "../services/graphService";
import { app } from "@microsoft/teams-js";
import {
  getMeetingReportDetail,
  registerMeeting,
  getTrackedMeetingStatus,
  checkMeetingTranscript,
  setEmailScope,
  CreditLimitError,
  type MeetingReport,
  type MeetingReportDetail,
  type TrackedMeetingStatus,
} from "../services/apiService";
import { openUpgradeLink } from "../teams/teamsClient";
import { config } from "../config";
import { sanitizeHtml } from "../services/sanitizeHtml";
import { useLanguage } from "../i18n/LanguageContext";

interface MeetingPanelProps {
  /** All upcoming meetings from the calendar. */
  meetings: MeetingEvent[];
  /** Teams chat/thread id of the current meeting (e.g. 19:meeting_...@thread.v2). */
  meetingChatId: string | null;
  /** Processed meetings from the Meeting Angelo database. */
  reports: MeetingReport[];
  /** App JWT for loading the current meeting's summary, when available. */
  jwt: string | null;
  /** Whether the app is hosted inside the Teams client (affects how external links open). */
  insideTeams: boolean;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

function formatDay(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * Compact view rendered inside the meeting side panel (and meeting stage).
 * Designed for the ~320px wide in-meeting panel. Shows the current meeting,
 * its Meeting Angelo processing state and — once ready — the real AI summary.
 */
export function MeetingPanel({ meetings, meetingChatId, reports, jwt, insideTeams }: MeetingPanelProps) {
  const { t } = useLanguage();
  // Teams join URLs embed the URL-encoded chat thread id, so we can match the
  // current meeting against the calendar and the processed-meetings database.
  const encodedChatId = meetingChatId ? encodeURIComponent(meetingChatId) : null;
  const current = encodedChatId
    ? meetings.find((m) => m.joinUrl?.includes(encodedChatId))
    : undefined;
  const currentReport = encodedChatId
    ? reports.find((r) => r.joinUrl?.includes(encodedChatId))
    : undefined;

  const [detail, setDetail] = useState<MeetingReportDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [tracked, setTracked] = useState<TrackedMeetingStatus | null>(null);
  const [creditError, setCreditError] = useState<CreditLimitError | null>(null);
  const [sendToAll, setSendToAll] = useState(false);
  const [savingScope, setSavingScope] = useState(false);

  // RSC model (like Read.ai): the app being added to this meeting already
  // granted per-meeting transcript access, so a single idempotent register
  // call activates automatic tracking — no tenant-wide setup needed.
  useEffect(() => {
    if (!jwt || !meetingChatId) return;
    let cancelled = false;
    (async () => {
      try {
        const status = await registerMeeting(jwt, meetingChatId);
        if (!cancelled) {
          setTracked(status);
          setCreditError(null);
        }
        // Hydrate the persisted email-scope preference for the toggle.
        const full = await getTrackedMeetingStatus(jwt, meetingChatId).catch(() => null);
        if (!cancelled && full) setSendToAll(Boolean(full.sendToAll));
      } catch (err) {
        if (err instanceof CreditLimitError) {
          if (!cancelled) setCreditError(err);
          return;
        }
        try {
          const status = await getTrackedMeetingStatus(jwt, meetingChatId);
          if (!cancelled) {
            setTracked(status);
            setSendToAll(Boolean(status.sendToAll));
          }
        } catch {
          // Backend unreachable: panel still works in read-only mode.
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [jwt, meetingChatId]);

  // When tracking is active, look for the transcript once the summary is not
  // ready yet (covers delayed Graph notifications after the meeting ends).
  useEffect(() => {
    if (!jwt || !meetingChatId) return;
    if (tracked?.status !== "Subscribed" && tracked?.status !== "TranscriptReceived") return;
    let cancelled = false;
    checkMeetingTranscript(jwt, meetingChatId)
      .then((r) => {
        if (!cancelled && r.success && r.eventId) {
          setTracked((t) => (t ? { ...t, status: "Processed", eventId: r.eventId } : t));
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [jwt, meetingChatId, tracked?.status]);

  const trackedEventId = tracked?.eventId ?? currentReport?.eventId ?? null;
  const summaryReady = tracked?.status === "Processed" || Boolean(currentReport?.isTranscript);

  useEffect(() => {
    if (!jwt || !trackedEventId || !summaryReady) return;
    let cancelled = false;
    setLoadingDetail(true);
    getMeetingReportDetail(jwt, trackedEventId)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoadingDetail(false);
      });
    return () => {
      cancelled = true;
    };
  }, [jwt, trackedEventId, summaryReady]);

  const openFullApp = async () => {
    try {
      await app.openLink(mainAppUrl);
    } catch {
      window.open(mainAppUrl, "_blank", "noopener,noreferrer");
    }
  };

  const onToggleSendToAll = async (next: boolean) => {
    if (!jwt || !meetingChatId) return;
    setSendToAll(next); // optimistic
    setSavingScope(true);
    try {
      const r = await setEmailScope(jwt, meetingChatId, next);
      setSendToAll(Boolean(r.sendToAll));
    } catch {
      setSendToAll(!next); // revert on failure
    } finally {
      setSavingScope(false);
    }
  };

  const recentReady = reports.filter((r) => r.isTranscript).slice(0, 3);

  const teamsStoreUrl = `https://teams.microsoft.com/l/app/${config.teamsAppId}`;

  const creditErrorCopy = (error: CreditLimitError) => {
    switch (error.reason) {
      case "NoActiveSubscription":
        return {
          title: t("meetingPanel.creditErrors.noSubTitle"),
          description: t("meetingPanel.creditErrors.noSubBody"),
          ctaLabel: t("meetingPanel.creditErrors.noSubCta"),
          onCta: () => void openUpgradeLink(teamsStoreUrl, insideTeams),
        };
      case "PoolExhausted":
        return {
          title: t("meetingPanel.creditErrors.poolTitle"),
          description: t("meetingPanel.creditErrors.poolBody", { total: error.tenantMonthlyCredits }),
          ctaLabel: t("meetingPanel.creditErrors.poolCta"),
          onCta: () => {
            window.location.hash = "settings";
          },
        };
      case "UserLimitReached":
      default:
        return {
          title: t("meetingPanel.creditErrors.userTitle"),
          description: t("meetingPanel.creditErrors.userBody"),
          ctaLabel: t("meetingPanel.creditErrors.userCta"),
          onCta: () => {
            window.location.hash = "settings";
          },
        };
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", padding: "0.75rem" }}>
      <Card style={{ padding: "0.9rem", borderLeft: "3px solid var(--colorBrandBackground)" }}>
        <Text size={200} style={{ color: "var(--colorNeutralForeground3)" }}>
          {t("meetingPanel.thisMeeting")}
        </Text>
        <Text weight="semibold" block style={{ marginTop: "0.15rem" }}>
          {current?.subject ?? currentReport?.name ?? t("meetingPanel.meetingInProgress")}
        </Text>
        {current && (
          <Text size={200} block style={{ color: "var(--colorNeutralForeground3)" }}>
            {formatTime(current.start)}–{formatTime(current.end)} · {current.organizer}
            {current.attendeeCount > 0 ? ` · ${current.attendeeCount} ${t("meetings.attendees")}` : ""}
          </Text>
        )}
        <div style={{ marginTop: "0.5rem" }}>
          {creditError ? (
            <Badge appearance="tint" color="danger" size="small">
              {creditErrorCopy(creditError).title}
            </Badge>
          ) : summaryReady ? (
            <Badge appearance="tint" color="success" size="small">
              {t("meetingPanel.trackedReady", { state: detail?.hasSummary ? t("meetingPanel.ready") : t("meetingPanel.pending") })}
            </Badge>
          ) : tracked?.registered && tracked.status !== "Failed" ? (
            <Badge appearance="tint" color="brand" size="small">
              {t("meetingPanel.trackedWaitingSummary")}
            </Badge>
          ) : currentReport ? (
            <Badge appearance="tint" color="brand" size="small">
              {t("meetingPanel.trackedWaitingTranscript")}
            </Badge>
          ) : (
            <Badge appearance="tint" color="informative" size="small">
              {t("meetingPanel.activating")}
            </Badge>
          )}
        </div>
        {tracked?.registered && tracked.status !== "Failed" && !summaryReady && (
          <Text size={200} block style={{ marginTop: "0.4rem", color: "var(--colorNeutralForeground3)" }}>
            {t("meetingPanel.turnOnTranscription")}
          </Text>
        )}
      </Card>

      {creditError && (
        <Card style={{ padding: "0.9rem", borderLeft: "3px solid var(--colorPaletteRedForeground1)" }}>
          <Text weight="semibold" block>
            {creditErrorCopy(creditError).title}
          </Text>
          <Text size={200} block style={{ marginTop: "0.3rem", color: "var(--colorNeutralForeground3)" }}>
            {creditErrorCopy(creditError).description}
          </Text>
          <Button
            appearance="primary"
            size="small"
            style={{ marginTop: "0.6rem" }}
            onClick={creditErrorCopy(creditError).onCta}
          >
            {creditErrorCopy(creditError).ctaLabel}
          </Button>
        </Card>
      )}

      {tracked?.registered && tracked.status !== "Failed" && (
        <Card style={{ padding: "0.9rem" }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "0.75rem" }}>
            <div style={{ minWidth: 0 }}>
              <Text weight="semibold" block>
                {t("meetingPanel.emailToEveryone")}
              </Text>
              <Text size={200} block style={{ color: "var(--colorNeutralForeground3)", marginTop: "0.15rem" }}>
                {sendToAll
                  ? t("meetingPanel.sentToEveryone")
                  : t("meetingPanel.sentToYou")}
              </Text>
            </div>
            <Switch
              checked={sendToAll}
              disabled={savingScope}
              onChange={(_, data) => onToggleSendToAll(data.checked)}
              aria-label={t("meetingPanel.emailToEveryone")}
            />
          </div>
        </Card>
      )}

      {loadingDetail && (
        <Card style={{ padding: "0.9rem" }}>
          <Spinner size="tiny" label={t("meetingPanel.loadingSummary")} />
        </Card>
      )}

      {detail?.hasSummary && (
        <Card style={{ padding: "0.9rem" }}>
          <Text weight="semibold" block>
            {t("meetingPanel.aiSummary")}
          </Text>
          <Divider style={{ margin: "0.5rem 0" }} />
          <div
            style={{ fontSize: "0.8rem", lineHeight: 1.5, overflowWrap: "anywhere" }}
            // Sanitized output of our own backend's AI summary HTML.
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(detail.summaryHtml) }}
          />
        </Card>
      )}

      {recentReady.length > 0 && !detail?.hasSummary && (
        <Card style={{ padding: "0.9rem" }}>
          <Text weight="semibold" block>
            {t("meetingPanel.recentReports")}
          </Text>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", marginTop: "0.5rem" }}>
            {recentReady.map((r) => (
              <Text key={r.eventId} size={200} block>
                {formatDay(r.startDateTime)} · {r.name}
              </Text>
            ))}
          </div>
        </Card>
      )}

      <Card style={{ padding: "0.9rem" }}>
        <Text weight="semibold" block>
          {t("meetingPanel.wantMore")}
        </Text>
        <Text size={200} block style={{ margin: "0.3rem 0 0.6rem", color: "var(--colorNeutralForeground2)" }}>
          {t("meetingPanel.wantMoreBody")}
        </Text>
        <Button appearance="primary" size="small" onClick={openFullApp}>
          {t("meetingPanel.openFullApp")}
        </Button>
      </Card>
    </div>
  );
}
